/**
 * TODO: MAKE THIS INTO AN IndividualsContext or something so it can fetch information about any role
 */
/* eslint-disable @typescript-eslint/camelcase */
import React, { createContext, useCallback, useContext, useState } from "react";

import { DEFAULT_MAX_MENTORS } from "src/views/mentor/shift-schedule/constants";
import { useUpdateMentorShiftMutation } from "src/views/mentor/shifts/graphql/updateMentorShift.generated";

import { BaseShiftState, convertDayToDate } from "../constants";

interface EditMentorShiftState extends BaseShiftState {
  updateShift: (shiftId: number) => Promise<void>;
}

const DEFAULT_STATE: EditMentorShiftState = {
  shiftName: "",
  shiftDateStart: "",
  shiftDateEnd: "",
  timeStart: "",
  timeEnd: "",
  location: "",
  requiredMentors: DEFAULT_MAX_MENTORS,

  updateShiftName: () => Promise.reject(),
  updateShiftDateStart: () => Promise.reject(),
  updateShiftDateEnd: () => Promise.reject(),
  updateTimeStart: () => Promise.reject(),
  updateTimeEnd: () => Promise.reject(),
  updateLocation: () => Promise.reject(),
  updateRequiredMentors: () => Promise.reject(),

  updateShift: (shiftId: number) => Promise.reject(),
};

const EditMentorShiftContext: React.Context<EditMentorShiftState> =
  createContext(DEFAULT_STATE);

export const useEditMentorShiftContext = () =>
  useContext(EditMentorShiftContext);

export const EditMentorShiftContextProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<EditMentorShiftState>(() => DEFAULT_STATE);
  const [updateShiftMutation] = useUpdateMentorShiftMutation();

  const updateShiftName = useCallback((name: string) => {
    setData((prevState) => ({ ...prevState, shiftName: name }));
  }, []);

  const updateShiftDateStart = useCallback((date: string) => {
    setData((prevState) => ({ ...prevState, shiftDateStart: date }));
  }, []);

  const updateShiftDateEnd = useCallback((date: string) => {
    setData((prevState) => ({ ...prevState, shiftDateEnd: date }));
  }, []);

  const updateTimeStart = useCallback((time: string) => {
    setData((prevState) => ({ ...prevState, timeStart: time }));
  }, []);

  const updateTimeEnd = useCallback((time: string) => {
    setData((prevState) => ({ ...prevState, timeEnd: time }));
  }, []);

  const updateLocation = useCallback((location: string) => {
    setData((prevState) => ({ ...prevState, location }));
  }, []);

  const updateRequiredMentors = useCallback((mentors: number) => {
    setData((prevState) => ({ ...prevState, requiredMentors: mentors }));
  }, []);

  const updateShift = useCallback(
    async (shiftId: number): Promise<void> => {
      try {
        const dayStart = convertDayToDate(data.shiftDateStart);
        const dayEnd = convertDayToDate(data.shiftDateEnd);

        await updateShiftMutation({
          variables: {
            id: shiftId,
            name: data.shiftName,
            start_time: new Date(`${dayStart}T${data.timeStart}:00`),
            end_time: new Date(`${dayEnd}T${data.timeEnd}:00`),
            location: data.location,
            max_count: data.requiredMentors,
          },
        });
      } catch (e) {
        console.error(e);
      }
    },
    [updateShiftMutation, data]
  );

  const state = {
    ...data,
    updateShiftName,
    updateShiftDateStart,
    updateShiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateLocation,
    updateRequiredMentors,
    updateShift,
  };

  return (
    <EditMentorShiftContext.Provider value={state}>
      {children}
    </EditMentorShiftContext.Provider>
  );
};
